import * as z from 'zod/mini'
import { ianaTimeZone } from '../schemas/common'
import { entityFields } from '../schemas/entity'
import { userFields } from '../schemas/user'

export const authLoginBody = z.object({
  /** Not `emailField`: system accounts sign in as `sysadmin`, `email-reader`. Lowercased like every address. */
  email: z.string().check(z.trim(), z.toLowerCase(), z.minLength(4)),
  password: z.string().check(z.minLength(1)),
  /** Sign in to the admin app; refused for an account that is not root. */
  root: z.optional(z.boolean()),
})
export type AuthLoginBody = z.infer<typeof authLoginBody>

export const resetPasswordBody = z.object({ email: userFields.email })
export type ResetPasswordBody = z.infer<typeof resetPasswordBody>

export const setNewPasswordBody = z.object({
  token: z.string().check(z.minLength(1)),
  password: userFields.newPassword,
})
export type SetNewPasswordBody = z.infer<typeof setNewPasswordBody>

/** A signed-in person changing their own password on Môj profil (docs/account.md). */
export const changePasswordBody = z.object({
  currentPassword: z.string().check(z.minLength(1)),
  newPassword: userFields.newPassword,
})
export type ChangePasswordBody = z.infer<typeof changePasswordBody>

/** A signed-in person asking to sign in with another e-mail; a link goes to the new address (docs/account.md). */
export const requestEmailChangeBody = z.object({
  newEmail: userFields.email,
  currentPassword: z.string().check(z.minLength(1)),
})
export type RequestEmailChangeBody = z.infer<typeof requestEmailChangeBody>

/** The link from the confirmation e-mail. Public: it may be opened in a browser that is not signed in. */
export const confirmEmailChangeBody = z.object({ token: z.string().check(z.minLength(1)) })
export type ConfirmEmailChangeBody = z.infer<typeof confirmEmailChangeBody>

const registerFields = {
  /** The invited user's id, when registering from an invitation link. */
  id: z.optional(z.uuid()),
  email: userFields.email,
  password: userFields.newPassword,
  firstName: userFields.firstName,
  lastName: userFields.lastName,
  phone: userFields.phone,
  birthDate: userFields.birthDate,
  gender: userFields.gender,
}

export const authRegisterBody = z.object(registerFields)
export type AuthRegisterBody = z.infer<typeof authRegisterBody>

const companyText = z.string().check(z.trim(), z.maxLength(255))

/** Registers a user together with their premise (the owner's sign-up). */
export const authRegisterEntityBody = z.object({
  ...registerFields,
  name: entityFields.name,
  companyName: companyText,
  companyId: z.optional(companyText),
  vatId: z.optional(companyText),
  iban: z.optional(companyText),
  timezone: ianaTimeZone,
})
export type AuthRegisterEntityBody = z.infer<typeof authRegisterEntityBody>
